import { omit } from "lodash";
import { DateTime } from "luxon";
import React, { FC } from "react";

import { Card, CardContent, Skeleton } from "@mui/material";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import {
  AllSeriesType,
  AxisConfig,
  ChartContainer,
  ChartsGrid,
  ChartsTooltip,
  ChartsXAxis,
  ChartsXAxisProps,
  ChartsYAxis,
  LinePlot,
} from "@mui/x-charts";

import useTimePeriod from "../../../hooks/use-time-period";
import { useSelector } from "../../../store";
import {
  GetTimeSeriesApiArg,
  useGetTimeSeriesQuery,
} from "../../../store/pomeriumApi";

interface BytesProps {
  direction: "sent" | "received";
}

const Bytes: FC<BytesProps> = ({ direction }) => {
  const { period } = useTimePeriod();
  const { organizationId, clusterId } = useSelector((state) => state.current);
  const { routeId } = useSelector((state) => state.metrics);
  const now = DateTime.now().startOf("minute");
  const start = now.minus(period);
  let params: GetTimeSeriesApiArg = {
    organizationId,
    clusterId,
    startTime: start.toISO(),
    endTime: now.toISO(),
    routeId: routeId,
    metricId: direction === "sent" ? "upstream_tx_bytes" : "upstream_rx_bytes",
  };
  if (!routeId) {
    params = omit(params, ["routeId"]);
  }

  const { data: bytes } = useGetTimeSeriesQuery(params, {
    skip: !organizationId || !clusterId,
  });

  const transformed = bytes?.map((ts) => {
    return {
      series: {
        id: "bytes-" + direction,
        label: "Bytes " + direction,
        type: "line",
        data: ts.points.map((p) => p.value),
      } as AllSeriesType<"line">,
      xAxis: {
        id: "bytes-x-" + direction,
        data: ts.points.map((p) => new Date(p.timestamp)),
        scaleType: "utc",
        min: start.toJSDate(),
        max: now.toJSDate(),
      } as AxisConfig<"utc", any, ChartsXAxisProps>,
    };
  });

  return (
    <Card
      key={direction + "-bytes-chart"}
      sx={{
        width: "100%",
        m: 2,
      }}
    >
      <CardContent sx={{ height: "40vh", m: 2 }}>
        {" "}
        <Typography variant={"h4"} color="text.primary" gutterBottom>
          {"Bytes " + direction}
        </Typography>
        {!bytes && (
          <Skeleton width="100%" height="100%" sx={{ transform: "unset" }} />
        )}
        {bytes && !bytes?.length && (
          <Alert severity="info">
            No data to display. Data is updated hourly.
          </Alert>
        )}
        {transformed && !!bytes?.length && (
          <ChartContainer
            xAxis={
              transformed.map((d) => d.xAxis) as AxisConfig<
                "utc",
                any,
                ChartsXAxisProps
              >[]
            }
            series={transformed.map((d) => d.series)}
          >
            <LinePlot />
            <ChartsXAxis axisId={"bytes-x-" + direction} />
            <ChartsYAxis />
            <ChartsGrid horizontal />
            <ChartsTooltip trigger="axis" />
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default Bytes;
